import { cn } from "@/lib/utils";
import {
  STATUS_DEFAULT_LABELS,
  STATUS_ORDER,
  STATUS_SHAPES,
  type StatusKey,
} from "@/lib/status";

// The core check-in pattern: stacked, full-width Green/Yellow/Red buttons of
// identical visual weight. Selected = 2px status border + soft tint + AA text.
// Red carries no more alarm than Green — honesty, not failure.
const SELECTED: Record<StatusKey, string> = {
  green: "border-status-green bg-status-green-bg text-status-green-text",
  yellow: "border-status-yellow bg-status-yellow-bg text-status-yellow-text",
  red: "border-status-red bg-status-red-bg text-status-red-text",
};

const HOVER: Record<StatusKey, string> = {
  green: "hover:border-status-green/50",
  yellow: "hover:border-status-yellow/50",
  red: "hover:border-status-red/50",
};

const ICON_SELECTED: Record<StatusKey, string> = {
  green: "text-status-green",
  yellow: "text-status-yellow",
  red: "text-status-red",
};

export interface StatusButtonProps {
  status: StatusKey;
  selected?: boolean;
  label?: string;
  onClick?: () => void;
  className?: string;
}

export function StatusButton({
  status,
  selected = false,
  label,
  onClick,
  className,
}: StatusButtonProps) {
  const Shape = STATUS_SHAPES[status];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex min-h-[44px] w-full items-center gap-3 rounded-lg border-2 px-6 py-4 text-left text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        selected
          ? SELECTED[status]
          : cn("border-border text-muted-foreground", HOVER[status]),
        className
      )}
    >
      <Shape
        className={cn(
          "h-[18px] w-[18px] shrink-0",
          selected ? ICON_SELECTED[status] : "text-muted-foreground"
        )}
        strokeWidth={2}
      />
      <span>{label ?? STATUS_DEFAULT_LABELS[status]}</span>
    </button>
  );
}

export interface StatusGroupProps {
  value?: StatusKey;
  onChange?: (value: StatusKey) => void;
  labels?: Partial<Record<StatusKey, string>>;
  className?: string;
}

export function StatusGroup({
  value,
  onChange,
  labels,
  className,
}: StatusGroupProps) {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {STATUS_ORDER.map((s) => (
        <StatusButton
          key={s}
          status={s}
          selected={value === s}
          label={labels?.[s]}
          onClick={() => onChange?.(s)}
        />
      ))}
    </div>
  );
}
